import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Rating from '@mui/material/Rating';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import SentimentVeryDissatisfiedRoundedIcon from '@mui/icons-material/SentimentVeryDissatisfiedRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import SportsHandballRoundedIcon from '@mui/icons-material/SportsHandballRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import AppLayout from '../../components/ui/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import SectionCard from '../../components/ui/SectionCard';
import LoadingState from '../../components/ui/LoadingState';
import EmptyState from '../../components/ui/EmptyState';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import RankingList from '../../components/ui/RankingList';
import { cardColor } from '../jogos/GameCard';
import AbrirAvaliacaoDialog from '../../components/AbrirAvaliacaoDialog';
import { useAuth } from '../../AuthContext';
import { colocacaoDasNotas } from '../../utils/colocacaoNotas';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import LockClockRoundedIcon from '@mui/icons-material/LockClockRounded';
import PlayCircleRoundedIcon from '@mui/icons-material/PlayCircleRounded';
import api from '../../api';
import tokens from '../../theme/tokens';
import { dataPelada as formatarDataPelada } from '../../utils/dataPelada';

function tempoRestante(fechaEm) {
  const ms = new Date(fechaEm).getTime() - Date.now();
  if (ms <= 0) return 'encerrando...';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
}

const Destaque = ({ titulo, jogador, detalhe, icone, cor, fundo }) => (
  <Card sx={{ p: 3, height: '100%', textAlign: 'center', color: '#fff', border: 'none', background: fundo }}>
    {icone}
    <Typography variant="overline" sx={{ display: 'block', opacity: 0.85 }}>{titulo}</Typography>
    {jogador ? (
      <>
        <PlayerAvatar src={jogador.foto} name={jogador.nome} size={104} sx={{ mx: 'auto', my: 1.5, border: `4px solid ${cor}` }} />
        <Typography variant="h5">{jogador.nome}</Typography>
        <Typography sx={{ opacity: 0.85 }}>{detalhe}</Typography>
      </>
    ) : (
      <Typography sx={{ opacity: 0.8, mt: 2 }}>Sem votos suficientes</Typography>
    )}
  </Card>
);

// Quantos jogadores já avaliaram (do total que jogou) e quantas notas foram dadas.
const Participacao = ({ participacao, status }) => {
  if (!participacao || !participacao.totalJogadores) return null;
  const { avaliadores, totalJogadores, notas, notasPossiveis, goleiros = 0 } = participacao;
  const pct = Math.round((avaliadores / totalJogadores) * 100);
  return (
    <Card sx={{ p: { xs: 2, sm: 2.5 }, mb: 3, color: '#fff', border: 'none', background: `radial-gradient(circle at 0% 0%, rgba(233,168,0,0.28), transparent 55%), linear-gradient(160deg, ${tokens.color.inkSoft}, ${tokens.color.ink})` }}>
      <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="center">
        <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
          <CircularProgress variant="determinate" value={100} size={88} thickness={4} sx={{ color: 'rgba(255,255,255,0.12)' }} />
          <CircularProgress variant="determinate" value={pct} size={88} thickness={4} sx={{ color: tokens.color.gold, position: 'absolute', left: 0, '& circle': { strokeLinecap: 'round' } }} />
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 800, fontSize: 22 }}>{pct}%</Typography>
          </Box>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ opacity: 0.75, lineHeight: 1.4 }}>{status === 'encerrada' ? 'Participação final' : 'Participação até agora'}</Typography>
          <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 800, fontSize: { xs: 28, sm: 34 }, lineHeight: 1.1 }}>
            {avaliadores}<Box component="span" sx={{ opacity: 0.55, fontSize: '0.6em', fontWeight: 600 }}> de {totalJogadores}</Box>
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {avaliadores === 1 ? 'já avaliou' : 'já avaliaram'}
            {goleiros > 0 ? ` · inclui ${goleiros} ${goleiros === 1 ? 'goleiro' : 'goleiros'}` : ''}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75, opacity: 0.8 }}>
            <StarRoundedIcon sx={{ fontSize: 16, color: tokens.color.gold }} />
            <Typography variant="caption">{notas} de {notasPossiveis} notas dadas</Typography>
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
};

const CartaoNota = ({ pessoa, nota, onNota, goleiro = false, pendente = false }) => (
  <Stack
    id={`avaliar-${pessoa.user}`}
    direction="row"
    spacing={1.5}
    alignItems="center"
    sx={{
      p: 1.5,
      borderRadius: 2,
      border: pendente ? 2 : 1,
      borderStyle: pendente ? 'dashed' : 'solid',
      borderColor: nota ? tokens.color.brand : pendente ? tokens.color.warning : 'divider',
      bgcolor: nota ? tokens.color.brandSoft : pendente ? tokens.color.warningSoft : 'transparent',
    }}
  >
    {goleiro ? (
      <Box sx={{ width: 48, height: 48, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: tokens.color.inkSoft, color: '#fff', flexShrink: 0 }}>
        <SportsHandballRoundedIcon />
      </Box>
    ) : (
      <PlayerAvatar src={pessoa.foto} name={pessoa.nome} size={48} />
    )}
    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
      <Typography fontWeight={600} noWrap>{pessoa.nome}</Typography>
      <Rating
        value={nota || 0}
        onChange={(e, valor) => onNota(valor)}
        size="large"
        icon={<StarRoundedIcon fontSize="inherit" sx={{ color: tokens.color.gold }} />}
        emptyIcon={<StarBorderRoundedIcon fontSize="inherit" />}
      />
    </Box>
  </Stack>
);

const ListaCartoes = ({ titulo, cor, itens }) => (
  <SectionCard title={titulo} icon={<Box sx={{ width: 14, height: 18, borderRadius: 0.5, bgcolor: cor }} />} sx={{ height: '100%' }}>
    <RankingList items={itens} valueKey="quantidade" empty="Ninguém" />
  </SectionCard>
);

// Goleiros: gols sofridos (partidas) + nota dada pelos jogadores; inclui escalados sem partida registrada
const tabelaGoleiros = (resultado) => {
  const linhas = (resultado.goleiros || []).map((g) => ({ ...g }));
  (resultado.notasGoleiros || []).forEach((n) => {
    const linha = linhas.find((g) => g.nome === n.nome);
    if (linha) Object.assign(linha, { media: n.media, votos: n.votos });
    else linhas.push({ nome: n.nome, media: n.media, votos: n.votos });
  });
  return linhas;
};

const Resultado = ({ resultado }) => (
  <Stack spacing={3}>
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Destaque
          titulo="Crack da pelada"
          jogador={resultado.crack}
          detalhe={resultado.crack && `média ${resultado.crack.media} · ${resultado.crack.positivas} avaliações 4-5★`}
          icone={<StarRoundedIcon sx={{ fontSize: 40, color: tokens.color.gold }} />}
          cor={tokens.color.gold}
          fundo={`radial-gradient(circle at 50% 0%, rgba(233,168,0,0.4), transparent 60%), linear-gradient(160deg, ${tokens.color.inkSoft}, ${tokens.color.ink})`}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Destaque
          titulo="Bola murcha"
          jogador={resultado.bolaMurcha}
          detalhe={resultado.bolaMurcha && `média ${resultado.bolaMurcha.media} em ${resultado.bolaMurcha.votos} votos`}
          icone={<SentimentVeryDissatisfiedRoundedIcon sx={{ fontSize: 40, color: '#CBD5E1' }} />}
          cor="#94A3B8"
          fundo={`linear-gradient(160deg, #475569, ${tokens.color.inkSoft})`}
        />
      </Grid>
    </Grid>

    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <SectionCard title="Artilheiro" icon={<SportsSoccerRoundedIcon color="primary" />} sx={{ height: '100%' }}>
          <RankingList items={resultado.artilheiros} valueKey="gols" color={tokens.color.brand} empty="Nenhum gol registrado pelo árbitro" />
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <SectionCard title="Quem mais ganhou" icon={<EmojiEventsRoundedIcon sx={{ color: tokens.color.success }} />} sx={{ height: '100%' }}>
          <RankingList items={resultado.maisVitorias} valueKey="vitorias" color={tokens.color.success} empty="Sem partidas finalizadas" />
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <SectionCard title="Quem mais perdeu" icon={<TrendingDownRoundedIcon color="error" />} sx={{ height: '100%' }}>
          <RankingList items={resultado.maisDerrotas} valueKey="derrotas" color={tokens.color.danger} empty="Sem partidas finalizadas" />
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={4}>
        <ListaCartoes titulo="Cartão amarelo" cor={cardColor.amarelo} itens={resultado.cartoesAmarelos} />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListaCartoes titulo="Cartão azul" cor={cardColor.azul} itens={resultado.cartoesAzuis} />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListaCartoes titulo="Cartão vermelho" cor={cardColor.vermelho} itens={resultado.cartoesVermelhos || []} />
      </Grid>
    </Grid>

    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <SectionCard title="Goleiros" subtitle="Gols sofridos e nota dos jogadores" icon={<SportsHandballRoundedIcon color="primary" />} sx={{ height: "100%" }}>
          {tabelaGoleiros(resultado).length === 0 ? (
            <EmptyState title="Goleiros não informados pelo árbitro" />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Goleiro</TableCell>
                  <TableCell align="center">Partidas</TableCell>
                  <TableCell align="center">Gols sofridos</TableCell>
                  <TableCell align="center">Nota</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tabelaGoleiros(resultado).map((g) => (
                  <TableRow key={g.nome}>
                    <TableCell sx={{ fontWeight: 600 }}>{g.nome}</TableCell>
                    <TableCell align="center">{g.partidas ?? '-'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: 'error.main' }}>{g.golsSofridos ?? '-'}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      {g.media ? (
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                          <StarRoundedIcon sx={{ fontSize: 18, color: tokens.color.gold }} />
                          <Typography variant="body2" fontWeight={700}>{g.media}</Typography>
                          <Typography variant="caption" color="text.secondary">({g.votos})</Typography>
                        </Stack>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>
      </Grid>
      <Grid item xs={12} md={7}>
        <SectionCard title="Notas de todos" subtitle={`${resultado.totalAvaliadores} jogadores avaliaram · ${resultado.totalPartidas} partidas`} sx={{ height: '100%' }}>
          {resultado.avaliacoes.length === 0 ? (
            <EmptyState title="Ninguém avaliou esta pelada" />
          ) : (
            <NotasDeTodos avaliacoes={resultado.avaliacoes} />
          )}
        </SectionCard>
      </Grid>
    </Grid>
  </Stack>
);

// Lista na colocação do resultado: maior média entre quem recebeu o mínimo de notas (o 1º é o crack),
// depois quem recebeu poucas notas
const NotasDeTodos = ({ avaliacoes }) => {
  const { avaliacoes: ordenadas, minimoVotos } = colocacaoDasNotas(avaliacoes);
  const primeiroComPoucas = ordenadas.findIndex((a) => a.votos < minimoVotos);
  return (
            <Stack spacing={1}>
              {ordenadas.map((a, i) => (
                <React.Fragment key={a.user}>
                {i === primeiroComPoucas && (
                  <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
                    Com menos de {minimoVotos} notas (fora da disputa do crack)
                  </Typography>
                )}
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ width: 24, textAlign: 'right', flexShrink: 0 }}>
                    {a.votos >= minimoVotos ? `${i + 1}º` : ''}
                  </Typography>
                  <PlayerAvatar src={a.foto} name={a.nome} size={32} />
                  <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }} noWrap>{a.nome}</Typography>
                  <Rating value={a.media} precision={0.1} readOnly size="small" />
                  <Typography variant="body2" sx={{ width: 64, textAlign: 'right' }} color="text.secondary">{a.media} ({a.votos})</Typography>
                </Stack>
                </React.Fragment>
              ))}
            </Stack>
  );
};

// Avaliação (1 a 5 estrelas) dos jogadores que jogaram a pelada, e o resultado após o encerramento.
// Controles da diretoria: abrir a qualquer momento, encerrar antes do prazo e reabrir.
const PainelDiretoria = ({ dados, peladaId, onAtualizado, onMessage }) => {
  const [abrir, setAbrir] = useState(false);
  const [encerrar, setEncerrar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const status = dados.status;

  const confirmarEncerrar = async () => {
    setEnviando(true);
    try {
      const response = await api.post(`avaliacoes/${peladaId}/encerrar`);
      onAtualizado(response.data);
      onMessage({ severity: 'success', message: 'Avaliação encerrada e resultado calculado.' });
    } catch (error) {
      onMessage({ severity: 'error', message: error.response?.data?.error || 'Não foi possível encerrar.' });
    }
    setEnviando(false);
    setEncerrar(false);
  };

  return (
    <Card sx={{ p: 2, mb: 3, borderStyle: 'dashed' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Box>
          <Typography fontWeight={700}>Diretoria</Typography>
          <Typography variant="body2" color="text.secondary">
            {status === 'aberta' ? `Aberta até ${new Date(dados.fechaEm).toLocaleString('pt-BR')}` : status === 'encerrada' ? 'Avaliação encerrada' : 'Avaliação ainda não aberta'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {status === 'aberta' ? (
            <Button variant="outlined" color="secondary" startIcon={<LockClockRoundedIcon />} onClick={() => setEncerrar(true)} disabled={enviando}>Encerrar agora</Button>
          ) : (
            <Button variant="contained" startIcon={<PlayCircleRoundedIcon />} onClick={() => setAbrir(true)}>{status === 'encerrada' ? 'Reabrir avaliação' : 'Abrir avaliação agora'}</Button>
          )}
        </Stack>
      </Stack>
      <AbrirAvaliacaoDialog
        open={abrir}
        peladaId={peladaId}
        reabrir={status === 'encerrada'}
        onClose={() => setAbrir(false)}
        onAberta={(resumo) => {
          setAbrir(false);
          onAtualizado(resumo);
          onMessage({ severity: 'success', message: 'Avaliação aberta. Os jogadores foram notificados.' });
        }}
      />
      <Dialog open={encerrar} onClose={() => setEncerrar(false)}>
        <DialogTitle>Encerrar a avaliação agora?</DialogTitle>
        <DialogContent>
          <DialogContentText>Ninguém mais poderá votar. O resultado (crack, bola murcha etc.) é calculado com as notas atuais e todos recebem uma notificação.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEncerrar(false)} color="inherit">Cancelar</Button>
          <Button onClick={confirmarEncerrar} variant="contained" disabled={enviando}>Encerrar</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// Diretoria sem nenhuma avaliação: escolher uma pelada recente para abrir.
const EscolherPelada = ({ onEscolher }) => {
  const [peladas, setPeladas] = useState(null);
  useEffect(() => {
    api.get('avaliacoes/peladas/recentes').then((r) => setPeladas(r.data.peladas)).catch(() => setPeladas([]));
  }, []);
  if (!peladas) return <LoadingState minHeight={120} />;
  return (
    <SectionCard title="Abrir avaliação de uma pelada" subtitle="Como diretoria, você pode abrir a avaliação a qualquer momento.">
      <Stack spacing={1}>
        {peladas.map((p) => (
          <Stack key={p._id} direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.25, borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography fontWeight={600}>{formatarDataPelada(p.date, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })} · {p.time}</Typography>
              <Typography variant="caption" color="text.secondary">{p.jogadores} jogadores com check-in{p.status === 'Open' ? ' · pelada aberta' : ''}</Typography>
            </Box>
            {p.avaliacao && <Chip size="small" label={p.avaliacao === 'aberta' ? 'Avaliação aberta' : 'Encerrada'} color={p.avaliacao === 'aberta' ? 'warning' : 'default'} />}
            <Button size="small" variant="outlined" onClick={() => onEscolher(p._id)}>{p.avaliacao ? 'Ver' : 'Gerenciar'}</Button>
          </Stack>
        ))}
      </Stack>
    </SectionCard>
  );
};

const AvaliacaoPage = () => {
  const { peladaId } = useParams();
  const navigate = useNavigate();
  const { userLogado } = useAuth();
  const ehDiretoria = userLogado && (userLogado.perfil === 'Admin' || userLogado.perfil === 'Diretor');
  const ehGoleiro = userLogado?.perfil === 'Goleiro';
  const [dados, setDados] = useState(null);
  const [notas, setNotas] = useState({});
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [toast, setToast] = useState(null);
  const [semAvaliacao, setSemAvaliacao] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      if (!peladaId) {
        const response = await api.get('avaliacoes/ultima');
        if (response.data.pelada) navigate(`/avaliacao/${response.data.pelada._id}`, { replace: true });
        else setSemAvaliacao(true);
        return;
      }
      const response = await api.get(`avaliacoes/${peladaId}`);
      setDados(response.data);
      setNotas(response.data.minhasNotas || {});
    } catch (error) {
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, [peladaId, navigate]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const goleirosParaAvaliar = dados?.goleiros || [];
  // Obrigatório avaliar todos da lista (jogadores e goleiros escalados)
  const idsParaAvaliar = useMemo(
    () => [...(dados?.jogadores || []), ...(dados?.goleiros || [])].map((p) => String(p.user)),
    [dados]
  );
  const faltando = idsParaAvaliar.filter((id) => !notas[id]);
  const totalParaAvaliar = idsParaAvaliar.length;
  const avaliados = totalParaAvaliar - faltando.length;
  const [mostrarFaltando, setMostrarFaltando] = useState(false);

  const salvar = async () => {
    if (faltando.length > 0) {
      // Destaca quem falta e leva até o primeiro
      setMostrarFaltando(true);
      setToast({ severity: 'warning', message: `Avalie todos os jogadores para salvar. ${faltando.length === 1 ? 'Falta 1' : `Faltam ${faltando.length}`}.` });
      const el = document.getElementById(`avaliar-${faltando[0]}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSalvando(true);
    try {
      const lista = Object.entries(notas).filter(([, nota]) => nota).map(([avaliado, nota]) => ({ avaliado, nota }));
      await api.post(`avaliacoes/${peladaId}`, { notas: lista });
      setToast({ severity: 'success', message: 'Avaliação salva! Você pode ajustar as notas até o encerramento.' });
      api.get(`avaliacoes/${peladaId}`).then((r) => setDados((d) => ({ ...d, participacao: r.data.participacao }))).catch(() => {});
    } catch (error) {
      setToast({ severity: 'error', message: error.response?.data?.error || 'Não foi possível salvar.' });
    }
    setSalvando(false);
  };

  const dataPelada = formatarDataPelada(dados?.pelada?.date, { weekday: 'long', day: '2-digit', month: 'long' });

  return (
    <AppLayout>
      <PageHeader eyebrow="Pelada" title="Avaliação da pelada" subtitle={dataPelada ? `Pelada de ${dataPelada}` : 'Dê de 1 a 5 estrelas para quem jogou com você.'} />

      {carregando ? (
        <LoadingState />
      ) : semAvaliacao ? (
        ehDiretoria ? (
          <EscolherPelada onEscolher={(id) => navigate(`/avaliacao/${id}`)} />
        ) : (
          <Card><EmptyState icon={<StarBorderRoundedIcon />} title="Nenhuma avaliação disponível" description="A avaliação abre 30 minutos após o fim de cada pelada e fica aberta por 24 horas." /></Card>
        )
      ) : !dados ? (
        <Card><EmptyState title="Pelada não encontrada" /></Card>
      ) : (
        <>
          {ehDiretoria && (
            <PainelDiretoria
              dados={dados}
              peladaId={peladaId}
              onAtualizado={(resumo) => {
                setDados(resumo);
                setNotas(resumo.minhasNotas || {});
              }}
              onMessage={setToast}
            />
          )}
          {dados.status !== 'aguardando' && <Participacao participacao={dados.participacao} status={dados.status} />}
          {dados.status === 'encerrada' && dados.resultado ? (
        <Resultado resultado={dados.resultado} />
      ) : dados.status === 'aguardando' ? (
        <Card><EmptyState icon={<HourglassTopRoundedIcon />} title="Avaliação ainda não aberta" description={ehDiretoria ? 'Use "Abrir avaliação agora" acima para liberar os votos.' : 'Ela abre 30 minutos após o fim da pelada. Você receberá uma notificação.'} /></Card>
      ) : (
        <Stack spacing={3}>
          <Alert severity="info" icon={<HourglassTopRoundedIcon />} sx={{ alignItems: 'center' }}>
            Avaliação aberta · encerra em <strong>{tempoRestante(dados.fechaEm)}</strong>. As notas são anônimas.
          </Alert>
          {!dados.jogou ? (
            <Card><EmptyState title={ehGoleiro ? 'Só os goleiros escalados nesta pelada podem avaliar' : 'Só quem jogou esta pelada pode avaliar'} description="O resultado fica disponível para todos após o encerramento." /></Card>
          ) : (
            <SectionCard
              title={ehGoleiro ? 'Avalie os jogadores da pelada' : 'Avalie quem jogou com você'}
              subtitle={`${avaliados} de ${totalParaAvaliar} avaliados`}
              actions={<Chip color={avaliados === totalParaAvaliar ? 'success' : 'default'} label={`${Math.round((avaliados / Math.max(1, totalParaAvaliar)) * 100)}%`} />}
            >
              <LinearProgress variant="determinate" value={(avaliados / Math.max(1, totalParaAvaliar)) * 100} sx={{ height: 6, borderRadius: 3, mb: 2 }} />
              <Grid container spacing={1.5}>
                {dados.jogadores.map((j) => (
                  <Grid item xs={12} sm={6} lg={4} key={j.user}>
                    <CartaoNota pessoa={j} nota={notas[j.user]} pendente={mostrarFaltando && !notas[j.user]} onNota={(valor) => setNotas({ ...notas, [j.user]: valor })} />
                  </Grid>
                ))}
              </Grid>
              {goleirosParaAvaliar.length > 0 && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3, mb: 1.5 }}>
                    <SportsHandballRoundedIcon color="primary" />
                    <Typography fontWeight={700}>Goleiros da pelada</Typography>
                  </Stack>
                  <Grid container spacing={1.5}>
                    {goleirosParaAvaliar.map((g) => (
                      <Grid item xs={12} sm={6} lg={4} key={g.user}>
                        <CartaoNota goleiro pessoa={g} nota={notas[g.user]} pendente={mostrarFaltando && !notas[g.user]} onNota={(valor) => setNotas({ ...notas, [g.user]: valor })} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="flex-end" sx={{ mt: 3 }}>
                {faltando.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                    É obrigatório avaliar todos · {faltando.length === 1 ? 'falta 1' : `faltam ${faltando.length}`}
                  </Typography>
                )}
                <Button variant="contained" size="large" onClick={salvar} disabled={salvando}>
                  {faltando.length > 0 ? `Salvar avaliação (${avaliados}/${totalParaAvaliar})` : 'Salvar avaliação'}
                </Button>
              </Stack>
            </SectionCard>
          )}
        </Stack>
      )}
        </>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert variant="filled" severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </AppLayout>
  );
};

export default AvaliacaoPage;
